import React, { useState, useEffect, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControlLabel,
    Switch,
    Box,
    Alert,
    CircularProgress,
    Typography,
    IconButton,
    Link,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    CloudUpload,
    Delete as DeleteIcon,
    Close,
    OpenInNew,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Brand } from "../../types";
import { productService } from "../../services/productService";
import { STOREFRONT_HOST, brandPageUrl } from "../../config/storefront";
import { SLUG_RE, cleanSlugInput, slugFromName } from "../../utils/slug";


const schema = yup.object({
    name: yup.string().required("Brand name is required"),
    slug: yup
        .string()
        .optional()
        .max(120, "Keep it under 120 characters")
        .test(
            "slug",
            "Use lowercase letters, numbers and single hyphens (e.g. smart-heart)",
            (v) => !v || SLUG_RE.test(v),
        ),
    description: yup.string().optional(),
    display_order: yup.number().min(0, "Must be 0 or greater").optional(),
    is_active: yup.boolean().optional(),
    is_featured: yup.boolean().optional(),
});

interface BrandFormData {
    name: string;
    slug?: string;
    description?: string;
    display_order?: number;
    is_active: boolean;
    is_featured: boolean;
}

interface BrandFormProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (brand: Brand) => void;
    brand?: Brand | null;
    mode: "create" | "edit";
}

export const BrandForm: React.FC<BrandFormProps> = ({
    open,
    onClose,
    onSubmit,
    brand,
    mode,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const [imageUploading, setImageUploading] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
    const [coverUploading, setCoverUploading] = useState(false);
    const [currentCoverUrl, setCurrentCoverUrl] = useState<string | null>(null);
    // While false (a new brand whose address hasn't been typed), the web
    // address follows the name.
    const [slugEdited, setSlugEdited] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const coverInputRef = useRef<HTMLInputElement>(null);
    // The brand as the API last returned it, when the logo or cover changed
    // here — the list behind the form needs refreshing even on Cancel.
    const changedBrandRef = useRef<Brand | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        formState: { errors },
    } = useForm<BrandFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            slug: "",
            description: "",
            display_order: 0,
            is_active: true,
            is_featured: false,
        },
    });

    useEffect(() => {
        if (open) {
            changedBrandRef.current = null;
            if (brand && mode === "edit") {
                reset({
                    name: brand.name,
                    slug: brand.slug || "",
                    description: brand.description || "",
                    display_order: brand.display_order ?? 0,
                    is_active: brand.is_active,
                    is_featured: brand.is_featured ?? false,
                });
                setCurrentImageUrl(brand.image_url || null);
                setCurrentCoverUrl(brand.cover_image_url || null);
                // Renaming a brand keeps its address.
                setSlugEdited(true);
            } else {
                reset({
                    name: "",
                    slug: "",
                    description: "",
                    display_order: 0,
                    is_active: true,
                    is_featured: false,
                });
                setCurrentImageUrl(null);
                setCurrentCoverUrl(null);
                setSlugEdited(false);
            }
        }
    }, [open, brand, mode, reset]);

    const slug = watch("slug") || "";
    const isActive = watch("is_active");

    const savedSlug = mode === "edit" ? brand?.slug || "" : "";
    const slugChanged = mode === "edit" && !!slug && slug !== savedSlug;

    const handleFormSubmit = async (data: BrandFormData) => {
        try {
            setLoading(true);
            setError("");

            const { slug: typedSlug, ...rest } = data;
            const brandData = {
                ...rest,
                description: data.description || "",
                display_order: data.display_order ?? 0,
                is_active: data.is_active ?? true,
                is_featured: data.is_featured ?? false,
                // Only send an address staff chose; otherwise the API makes
                // one from the name (and keeps an existing one).
                ...(typedSlug &&
                (mode === "create" ? slugEdited : typedSlug !== savedSlug)
                    ? { slug: typedSlug }
                    : {}),
            };

            let result: Brand;
            if (mode === "create") {
                result = await productService.createBrand(brandData);
            } else {
                result = await productService.updateBrand(
                    brand!.brand_id,
                    brandData,
                );
            }

            onSubmit(result);
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || `Failed to ${mode} brand`);
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        if (!file || !brand?.brand_id) return;

        try {
            setImageUploading(true);
            setError("");
            const updated = await productService.uploadBrandImage(
                brand.brand_id,
                file,
            );
            changedBrandRef.current = updated;
            setCurrentImageUrl(updated.image_url || null);
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to upload image");
        } finally {
            setImageUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const handleImageDelete = async () => {
        if (!brand?.brand_id) return;

        try {
            setImageUploading(true);
            setError("");
            changedBrandRef.current = await productService.deleteBrandImage(
                brand.brand_id,
            );
            setCurrentImageUrl(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to delete image");
        } finally {
            setImageUploading(false);
        }
    };

    const handleCoverUpload = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        if (!file || !brand?.brand_id) return;

        try {
            setCoverUploading(true);
            setError("");
            const updated = await productService.uploadBrandCover(
                brand.brand_id,
                file,
            );
            changedBrandRef.current = updated;
            setCurrentCoverUrl(updated.cover_image_url || null);
        } catch (err: any) {
            setError(
                err.response?.data?.detail || "Failed to upload cover photo",
            );
        } finally {
            setCoverUploading(false);
            if (coverInputRef.current) coverInputRef.current.value = "";
        }
    };

    const handleCoverDelete = async () => {
        if (!brand?.brand_id) return;
        if (!window.confirm("Remove the cover photo from this brand's page?"))
            return;

        try {
            setCoverUploading(true);
            setError("");
            changedBrandRef.current = await productService.deleteBrandCover(
                brand.brand_id,
            );
            setCurrentCoverUrl(null);
        } catch (err: any) {
            setError(
                err.response?.data?.detail || "Failed to remove cover photo",
            );
        } finally {
            setCoverUploading(false);
        }
    };

    const handleClose = () => {
        const changed = changedBrandRef.current;
        reset();
        setError("");
        setCurrentImageUrl(null);
        setCurrentCoverUrl(null);
        if (changed) {
            // The logo or cover was saved straight away — let the list
            // pick it up.
            onSubmit(changed);
        }
        onClose();
    };

    const slugHelper = () => {
        if (errors.slug?.message) return errors.slug.message;
        if (!slug) {
            return mode === "create"
                ? "Made from the brand name when you save"
                : "Leave empty to keep the current address";
        }
        const address = `${STOREFRONT_HOST}/shop?brand=${slug}`;
        // The saved address of a brand that's on the website — open it.
        if (mode === "edit" && !slugChanged && brand?.is_active) {
            return (
                <Link
                    href={brandPageUrl(slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        wordBreak: "break-all",
                    }}
                >
                    {address}
                    <OpenInNew sx={{ fontSize: 14 }} />
                </Link>
            );
        }
        return address;
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pr: 1,
                }}
            >
                {mode === "create" ? "Add New Brand" : "Edit Brand"}
                {isMobile && (
                    <IconButton
                        edge="end"
                        onClick={handleClose}
                        aria-label="close"
                        size="small"
                    >
                        <Close />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" sx={{ mt: 1 }}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    // A new brand's address follows its name
                                    // until it's typed in.
                                    if (!slugEdited) {
                                        setValue("slug", slugFromName(e.target.value));
                                    }
                                }}
                                margin="normal"
                                required
                                fullWidth
                                label="Brand Name"
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="slug"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                onChange={(e) => {
                                    setSlugEdited(true);
                                    field.onChange(cleanSlugInput(e.target.value));
                                }}
                                margin="normal"
                                fullWidth
                                label="Web address"
                                placeholder="e.g. smart-heart"
                                inputProps={{ maxLength: 120, spellCheck: false }}
                                error={!!errors.slug}
                                helperText={slugHelper()}
                                FormHelperTextProps={{
                                    sx: { wordBreak: "break-all" },
                                }}
                            />
                        )}
                    />
                    {slugChanged && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            Links to the old address ({STOREFRONT_HOST}
                            /shop?brand={savedSlug}) will stop working —
                            including any shared on Facebook or in ads.
                        </Alert>
                    )}
                    {mode === "edit" && !isActive && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                        >
                            Inactive brands don't have a page on the website.
                        </Typography>
                    )}

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                error={!!errors.description}
                                helperText={
                                    errors.description?.message ||
                                    "Shown under the brand name on its page. **Bold** and lists work."
                                }
                            />
                        )}
                    />

                    <Controller
                        name="display_order"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Display Order"
                                type="number"
                                inputProps={{ min: 0 }}
                                error={!!errors.display_order}
                                helperText={
                                    errors.display_order?.message ||
                                    "Lower numbers appear first"
                                }
                            />
                        )}
                    />

                    {mode === "create" && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 2 }}
                        >
                            You can add the logo and cover photo after
                            creating the brand.
                        </Typography>
                    )}

                    {/* Image: upload UI in edit mode */}
                    {mode === "edit" && brand?.brand_id && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                                Brand Logo / Image
                            </Typography>
                            {currentImageUrl ? (
                                <Box
                                    sx={{
                                        position: "relative",
                                        display: "inline-block",
                                        mb: 1,
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={currentImageUrl}
                                        alt="Brand"
                                        sx={{
                                            maxWidth: 200,
                                            maxHeight: 200,
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            objectFit: "cover",
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={handleImageDelete}
                                        disabled={imageUploading}
                                        sx={{
                                            position: "absolute",
                                            top: -8,
                                            right: -8,
                                            bgcolor: "background.paper",
                                            boxShadow: 1,
                                            "&:hover": {
                                                bgcolor: "error.light",
                                                color: "white",
                                            },
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 1 }}
                                >
                                    No image uploaded
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={
                                        imageUploading ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <CloudUpload />
                                        )
                                    }
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    disabled={imageUploading}
                                >
                                    {currentImageUrl
                                        ? "Replace Image"
                                        : "Upload Image"}
                                </Button>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Max 5MB. Will be optimized to 400x400.
                                </Typography>
                            </Box>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleImageUpload}
                            />
                        </Box>
                    )}

                    {/* Cover photo: the wide banner on the brand's page */}
                    {mode === "edit" && brand?.brand_id && (
                        <Box sx={{ mt: 3, mb: 1 }}>
                            <Typography variant="subtitle2">
                                Cover Photo
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mb: 1 }}
                            >
                                The wide banner at the top of the brand's
                                page. Use a wide image, about 1920×480.
                                {currentCoverUrl &&
                                    " Phones show only the unshaded middle."}
                            </Typography>
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    aspectRatio: "4 / 1",
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    border: "1px solid",
                                    borderColor: "divider",
                                    bgcolor: "action.hover",
                                }}
                            >
                                {currentCoverUrl ? (
                                    <>
                                        <Box
                                            component="img"
                                            src={currentCoverUrl}
                                            alt="Brand cover"
                                            sx={{
                                                display: "block",
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                        {/* Phones crop a 4:1 cover to about 2.6:1 */}
                                        <Box
                                            aria-hidden
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                display: "flex",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: "17.5%",
                                                    bgcolor: "rgba(0,0,0,0.38)",
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    flex: 1,
                                                    borderLeft:
                                                        "1px dashed rgba(255,255,255,0.85)",
                                                    borderRight:
                                                        "1px dashed rgba(255,255,255,0.85)",
                                                }}
                                            />
                                            <Box
                                                sx={{
                                                    width: "17.5%",
                                                    bgcolor: "rgba(0,0,0,0.38)",
                                                }}
                                            />
                                        </Box>
                                    </>
                                ) : (
                                    <Box
                                        sx={{
                                            height: "100%",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            px: 2,
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No cover photo — the page shows a
                                            plain band instead
                                        </Typography>
                                    </Box>
                                )}
                                {coverUploading && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            bgcolor: "rgba(255,255,255,0.6)",
                                        }}
                                    >
                                        <CircularProgress size={28} />
                                    </Box>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    mt: 1,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<CloudUpload />}
                                    onClick={() =>
                                        coverInputRef.current?.click()
                                    }
                                    disabled={coverUploading}
                                >
                                    {currentCoverUrl
                                        ? "Replace Cover"
                                        : "Upload Cover"}
                                </Button>
                                {currentCoverUrl && (
                                    <Button
                                        size="small"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleCoverDelete}
                                        disabled={coverUploading}
                                    >
                                        Remove
                                    </Button>
                                )}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Max 8MB.
                                </Typography>
                            </Box>
                            <input
                                ref={coverInputRef}
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={handleCoverUpload}
                            />
                        </Box>
                    )}

                    <Box sx={{ display: "flex", gap: 3, mt: 2, flexWrap: "wrap" }}>
                        <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="Active"
                                />
                            )}
                        />
                        <Controller
                            name="is_featured"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                            color="warning"
                                        />
                                    }
                                    label="Featured on Homepage"
                                />
                            )}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : mode === "create" ? (
                        "Create"
                    ) : (
                        "Update"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
